'use client'

import { useMemo, useState } from 'react'
import { ToolField, ToolResultCard, ToolResultRow, ToolSelect, toolFmt, toolNum } from './tool-inputs'

// Rule-of-thumb cooling load ≈ 135 W/m² at a 2.4 m ceiling in a moderate climate,
// adjusted for sun exposure, ceiling height, and occupants. Not a Manual-J load calc.
const BASE_W_PER_M2 = 135
const SUN_FACTOR: Record<string, number> = { shaded: 0.9, average: 1.0, sunny: 1.15 }

// Common split-unit sizes (BTU/hr) to round up to.
const UNIT_SIZES = [9000, 12000, 18000, 24000, 30000, 36000, 48000]

export function AirconBtuCalculatorTool() {
  const [area, setArea] = useState('20')
  const [height, setHeight] = useState('2.4')
  const [sun, setSun] = useState('average')
  const [occupants, setOccupants] = useState('2')

  const r = useMemo(() => {
    const heightFactor = toolNum(height) > 0 ? toolNum(height) / 2.4 : 1
    const watts = toolNum(area) * BASE_W_PER_M2 * heightFactor * (SUN_FACTOR[sun] ?? 1)
      + Math.max(0, toolNum(occupants) - 2) * 100
    const btu = watts * 3.412
    const recommended = UNIT_SIZES.find((s) => s >= btu) ?? Math.ceil(btu / 6000) * 6000
    return { watts, btu, kw: watts / 1000, recommended }
  }, [area, height, sun, occupants])

  return (
    <div className="grid gap-6 md:grid-cols-[1fr_380px] md:items-start">
      <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Room</h2>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <ToolField label="Floor area" value={area} onChange={setArea} unit="m²" />
            <ToolField label="Ceiling height" value={height} onChange={setHeight} unit="m" step="0.1" />
          </div>
        </div>
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Conditions</h2>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <ToolSelect
              label="Sun exposure"
              value={sun}
              onChange={setSun}
              options={[
                { value: 'shaded', label: 'Shaded / south-facing' },
                { value: 'average', label: 'Average' },
                { value: 'sunny', label: 'Sunny / west-facing' },
              ]}
            />
            <ToolField label="People (regular)" value={occupants} onChange={setOccupants} unit="ppl" step="1" />
          </div>
        </div>
      </div>

      <ToolResultCard title="Your aircon sizing">
        <ToolResultRow label="Cooling load" value={`${toolFmt(r.kw, 2)} kW`} />
        <ToolResultRow label="Cooling load" value={`${Math.round(r.btu).toLocaleString('en-ZA')} BTU/hr`} />
        <ToolResultRow label="Recommended unit" value={`${r.recommended.toLocaleString('en-ZA')} BTU`} strong />
      </ToolResultCard>
    </div>
  )
}
